
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Clock, Edit, Calendar, X, Video, Phone } from 'lucide-react';
import { format } from 'date-fns';

// Import the LocalAppointment interface to ensure compatibility
interface LocalAppointment {
  id: string;
  patientId: string;
  doctorId: string;
  appointmentDate: string;
  appointmentTime: string;
  appointmentType: string;
  status: string;
  duration: number;
  notes?: string;
  patientName?: string;
  date?: string;
  type?: string;
  time?: string;
}

interface AppointmentsListProps {
  appointments: LocalAppointment[];
  onReschedule: (id: string) => void;
  onCancel: (id: string) => void;
}

const AppointmentsList: React.FC<AppointmentsListProps> = ({ 
  appointments, 
  onReschedule, 
  onCancel 
}) => {
  // Function to get icon based on appointment type
  const getAppointmentTypeIcon = (type: string) => {
    switch(type) {
      case 'video':
        return <Video className="h-4 w-4 text-blue-500" />;
      case 'voice':
        return <Phone className="h-4 w-4 text-purple-500" />;
      default:
        return <Calendar className="h-4 w-4 text-green-500" />;
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {appointments.length > 0 ? (
        appointments.map((appointment) => (
          <Card key={appointment.id} className="overflow-hidden">
            <CardHeader className="p-4 bg-muted/20">
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="text-lg font-medium">{appointment.patientName}</CardTitle>
                  <p className="text-sm text-muted-foreground">
                    <span className="flex items-center gap-1 mt-1">
                      {getAppointmentTypeIcon(appointment.type || appointment.appointmentType)}
                      {appointment.type || appointment.appointmentType}
                    </span>
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-medium text-sm">
                    {appointment.appointmentDate || (appointment.date ? format(new Date(appointment.date), 'MMM dd, yyyy') : 'N/A')}
                  </p>
                  <div className="flex items-center text-sm text-muted-foreground">
                    <Clock className="h-3 w-3 mr-1" />
                    {appointment.appointmentTime || appointment.time}
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-4">
              <div className="flex justify-between items-center">
                <p className="text-sm text-muted-foreground line-clamp-2">
                  {appointment.notes || 'No notes provided'}
                </p>
                <div className="flex space-x-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => onReschedule(appointment.id)}
                  >
                    <Edit className="h-3 w-3 mr-1" />
                    Reschedule
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="text-red-500 hover:text-red-700"
                    onClick={() => onCancel(appointment.id)}
                  >
                    <X className="h-3 w-3 mr-1" />
                    Cancel
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))
      ) : (
        <div className="col-span-2 p-8 text-center border rounded-md bg-muted/10">
          <p className="text-muted-foreground">No appointments match your filters</p>
          <Button className="mt-4" variant="outline">Clear Filters</Button>
        </div>
      )}
    </div>
  );
};

export default AppointmentsList;
